/**
 * CompleteRegistration - Gate for authenticated users without organization assignment
 *
 * Shown when a user (e.g., from Google SSO) is authenticated but has no organization
 * in their Firestore user document. Blocks platform access until they select an
 * organization and verify their Student ID.
 *
 * Part of Plan B: Post-Auth "Complete Registration" Gate
 * @see docs/GOOGLE_SSO_SIGNUP_PLANS.md
 */
import React, { useState, useEffect, useMemo } from 'react';
import { auth } from '../firebase';
import { signInWithEmailAndPassword, linkWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { HeadlineMedium, LabelMedium, BodyMedium } from './Typography';
import { fetchAllOrganizations, organizationRequiresVerifiedId } from '../utils/organizationUtils';
import { resolveApiBaseUrl } from '../utils/apiBaseUrl';
import { useAuth } from '../contexts/AuthContext';

const CompleteRegistration = () => {
  const { user, logout, refreshUserData } = useAuth();

  const [organizations, setOrganizations] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [userIdNumber, setUserIdNumber] = useState('');
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState('');
  const [duplicateEmailError, setDuplicateEmailError] = useState(false);
  const [linkPassword, setLinkPassword] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);

  // Check for pre-selected organization from URL (e.g. ?system=orgId)
  const urlParams = new URLSearchParams(window.location.search);
  const systemOrgId = urlParams.get('system');
  const isOrgPreSelected = Boolean(systemOrgId);
  // Hide State on unique signup links so it cannot be edited or seen.
  // Keep it hidden until orgs finish loading; show it only if the org ID did not match.
  const orgsHaveLoaded = !loadingOrgs && organizations.length > 0;
  const hideStateField = isOrgPreSelected && (!orgsHaveLoaded || Boolean(selectedOrganization));

  // Load organizations on mount
  useEffect(() => {
    const loadOrganizations = async () => {
      setLoadingOrgs(true);
      try {
        const orgs = await fetchAllOrganizations();
        setOrganizations(orgs);

        if (systemOrgId) {
          const preSelectedOrg = orgs.find((org) => org.id === systemOrgId);
          if (preSelectedOrg) {
            setSelectedState(preSelectedOrg.state);
            setSelectedOrganization(systemOrgId);
          }
        }
      } catch (err) {
        console.error('Error loading organizations:', err);
      } finally {
        setLoadingOrgs(false);
      }
    };

    loadOrganizations();
  }, [systemOrgId]);

  const availableStates = useMemo(() => {
    const states = [
      ...new Set(
        organizations.map((org) => org.state).filter((state) => state && state !== 'Unknown'))
    ];
    return states.sort();
  }, [organizations]);

  const filteredOrganizations = useMemo(() => {
    if (!selectedState) return organizations;
    return organizations.filter((org) => org.state === selectedState);
  }, [organizations, selectedState]);

  const getSelectedOrganization = () => {
    return organizations.find((org) => org.id === selectedOrganization);
  };

  const handleStateChange = (newState) => {
    setSelectedState(newState);
    setSelectedOrganization('');
    setUserIdNumber('');
  };

  const handleOrganizationChange = (organizationId) => {
    setSelectedOrganization(organizationId);
    setUserIdNumber('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!user) {
      setError('Session expired. Please sign in again.');
      return;
    }

    if (!selectedOrganization) {
      setError('Please select your organization.');
      return;
    }

    const selectedOrg = getSelectedOrganization();
    const requiresVerifiedId = organizationRequiresVerifiedId(selectedOrg);

    if (requiresVerifiedId && !userIdNumber.trim()) {
      const fieldLabel = selectedOrg?.registrationLabel || 'ID Number';
      setError(`Please enter your ${fieldLabel}.`);
      return;
    }

    setSubmitLoading(true);
    setDuplicateEmailError(false);

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${resolveApiBaseUrl()}/api/auth/complete-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          organizationId: selectedOrganization,
          ...(requiresVerifiedId ? { userId: userIdNumber.trim() } : {})
        })
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 409 && data.errorCode === 'duplicate_email') {
        setDuplicateEmailError(true);
        setError('');
        return;
      }

      if (response.status === 400 && data.errorCode === 'invalid_user_id') {
        setError(data.error || 'ID is not valid for this organization.');
        return;
      }

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save organization information');
      }

      // Refresh AuthContext so userOrganizations updates and gate is lifted
      await refreshUserData();
    } catch (err) {
      console.error('Error saving organization info:', err);
      setError(err.message || 'Failed to save organization information. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  /** Sign out when user cancels to avoid stuck state */
  const handleCancel = async () => {
    await logout();
  };

  /**
   * Link Google account to existing email/password account.
   * Signs in with password, then links Google - user can then use either method.
   */
  const handleLinkAccounts = async (e) => {
    e.preventDefault();
    setError('');
    if (!linkPassword.trim()) {
      setError('Please enter your password.');
      return;
    }
    setLinkLoading(true);
    try {
      // Sign in with email/password to switch to the existing account (UID-1)
      await signInWithEmailAndPassword(auth, user.email, linkPassword);
      // Now we're signed in as UID-1. Link Google so they can use either method.
      await linkWithPopup(auth.currentUser, new GoogleAuthProvider());
      // AuthContext will update via onAuthStateChanged; gate lifts
      await refreshUserData?.();
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Incorrect password. Please try again.');
      } else if (err.code === 'auth/invalid-email') {
        setError('This account was not created with email/password. Please sign out and sign in with your original method.');
      } else if (err.code === 'auth/popup-closed-by-user') {
        setError('Google sign-in was cancelled. Please try again.');
      } else {
        setError(err.message || 'Failed to link accounts. Please try again.');
      }
    } finally {
      setLinkLoading(false);
    }
  };

  if (!user) {
    return null; // AuthContext will redirect to sign-in
  }

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Left column - branding (matches Auth.js layout) */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{ backgroundColor: '#282828' }}
      >
        <div className="absolute inset-0 opacity-10" aria-hidden="true">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <pattern
              id="complete-reg-pattern"
              x="0"
              y="0"
              width="20"
              height="20"
              patternUnits="userSpaceOnUse"
            >
              <circle cx="10" cy="10" r="1" fill="white" opacity="0.3" />
            </pattern>
            <rect width="100" height="100" fill="url(#complete-reg-pattern)" />
          </svg>
        </div>
        <div className="relative z-10 flex flex-col justify-center pl-24 pr-12 py-16 text-white">
          <div className="mb-4">
            <img
              src="/images/storykin_logo.png"
              alt="STORYKIN"
              className="w-64 h-auto object-contain"
            />
          </div>
          <h1 className="text-5xl font-bold leading-tight mb-8">
            Almost there!
            <br />
            <span className="text-3xl">Complete your registration</span>
          </h1>
          <p className="text-xl text-white/90">
            Select your organization and verify your student ID to access Storykin.
          </p>
        </div>
      </div>

      {/* Right column - form */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center px-4 py-8">
        <div className="lg:hidden w-full max-w-md mb-8 text-center">
          <img
            src="/images/storykin_logo.png"
            alt="STORYKIN"
            className="w-48 h-auto object-contain mx-auto mb-6"
          />
          <h1 className="text-2xl font-bold text-blue-900 mb-2">Complete Your Registration</h1>
        </div>

        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-6">
              <HeadlineMedium color="blue-900">Welcome, {user.displayName || user.email?.split('@')[0]}!</HeadlineMedium>
              <BodyMedium color="gray-600" className="mt-2">
                To access Storykin, please select your organization and verify your student ID.
              </BodyMedium>
            </div>

            {/* role=alert causes screen readers to announce the error immediately (WCAG 3.3.1) */}
            {error && (
              <div id="cr-error" role="alert" className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                <BodyMedium color="red-700">{error}</BodyMedium>
              </div>
            )}

            {duplicateEmailError && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-md space-y-4">
                <HeadlineMedium color="gray-800">Link Your Accounts</HeadlineMedium>
                <BodyMedium color="gray-700">
                  An account with this email already exists. Enter your password to link your Google
                  account—you'll then be able to sign in with either method.
                </BodyMedium>
                <form onSubmit={handleLinkAccounts} className="space-y-3">
                  <div>
                    <label htmlFor="link-password" className="block text-sm font-medium text-gray-700">Password</label>
                    <input
                      id="link-password"
                      type="password"
                      value={linkPassword}
                      onChange={(e) => setLinkPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoComplete="current-password"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="flex-1 py-2 px-4 border border-gray-300 rounded-full bg-white hover:bg-gray-50"
                    >
                      <LabelMedium color="gray-700">Cancel</LabelMedium>
                    </button>
                    <button
                      type="submit"
                      disabled={linkLoading}
                      className="flex-1 py-2 px-4 border border-blue-500 rounded-full bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
                    >
                      <LabelMedium color="white">
                        {linkLoading ? 'Linking...' : 'Link Accounts'}
                      </LabelMedium>
                    </button>
                  </div>
                </form>
              </div>
            )}

            {!duplicateEmailError && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* State dropdown — omitted on unique signup links (?system=) */}
              {!hideStateField && (
              <div className="space-y-1">
                <label htmlFor="cr-state" className="block text-sm font-medium text-gray-700">Select your state</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" aria-hidden="true">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <select
                    id="cr-state"
                    required
                    aria-describedby={error ? 'cr-error' : undefined}
                    className={`block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      loadingOrgs ? 'bg-gray-100 cursor-not-allowed' : 'bg-gray-50'
                    }`}
                    value={selectedState}
                    onChange={(e) => handleStateChange(e.target.value)}
                    disabled={loadingOrgs}
                  >
                    <option value="">
                      {loadingOrgs ? 'Loading states...' : 'Choose your state'}
                    </option>
                    {availableStates.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              )}

              {/* Organization dropdown — shown after state is chosen, or immediately on unique signup links */}
              {(selectedState || hideStateField) && (
                <div className="space-y-1">
                  <label htmlFor="cr-org" className="block text-sm font-medium text-gray-700">
                    Select your organization
                    {isOrgPreSelected && (
                      <span className="ml-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                        Pre-selected
                      </span>
                    )}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" aria-hidden="true">
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                    </div>
                    <select
                      id="cr-org"
                      required
                      aria-describedby={error ? 'cr-error' : undefined}
                      className={`block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        loadingOrgs || isOrgPreSelected ? 'bg-gray-100 cursor-not-allowed' : 'bg-gray-50'
                      }`}
                      value={selectedOrganization}
                      onChange={(e) => handleOrganizationChange(e.target.value)}
                      disabled={loadingOrgs || isOrgPreSelected}
                    >
                      <option value="">
                        {loadingOrgs
                          ? 'Loading organizations...'
                          : isOrgPreSelected
                          ? 'Organization pre-selected'
                          : 'Choose your organization'}
                      </option>
                      {filteredOrganizations.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* ID number field - only when organization requires verified ID */}
              {selectedOrganization && organizationRequiresVerifiedId(getSelectedOrganization()) && (
                <div className="space-y-1">
                  <label htmlFor="cr-user-id" className="block text-sm font-medium text-gray-700">
                    {getSelectedOrganization()?.registrationLabel || 'Student ID'}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" aria-hidden="true">
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V4a2 2 0 114 0v2m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"
                        />
                      </svg>
                    </div>
                    <input
                      id="cr-user-id"
                      type="text"
                      required
                      aria-describedby={error ? 'cr-error' : undefined}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-gray-50 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={`Enter your ${
                        getSelectedOrganization()?.registrationLabel || 'Student ID'
                      }`}
                      value={userIdNumber}
                      onChange={(e) => setUserIdNumber(e.target.value)}
                    />
                  </div>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={submitLoading}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-full shadow-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <LabelMedium color="gray-700">Cancel</LabelMedium>
                </button>
                <button
                  type="submit"
                  disabled={submitLoading || !selectedOrganization}
                  className="flex-1 py-3 px-4 border border-blue-500 rounded-full shadow-sm bg-blue-500 text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <LabelMedium color="white">
                    {submitLoading ? 'Saving...' : 'Complete Registration'}
                  </LabelMedium>
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompleteRegistration;
